import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const TermsOfService = () => {
  const { t } = useTranslation(['common']);

  return (
    <div className="min-h-screen bg-uplivion-lightBlue/30 py-8">
      <div className="container mx-auto px-6 max-w-4xl">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="mb-8">
            <Link to="/" className="text-uplivion-blue hover:underline mb-4 inline-block">
              ← Zurück zur Startseite
            </Link>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Allgemeine Geschäftsbedingungen (AGB)</h1>
            <p className="text-gray-600">Gültig ab: 28. Juni 2025 | Version 1.1</p>
          </div>

          <div className="prose max-w-none">
            <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-8">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-orange-700">
                    <strong>Wichtiger Hinweis:</strong> uplivion befindet sich derzeit in der Beta-Testphase. 
                    Der Service kann Einschränkungen haben und ist noch nicht vollständig ausgereift. 
                    Durch die Nutzung erklären Sie sich bereit, bei der Verbesserung des Services mitzuwirken.
                  </p>
                </div>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">1. Geltungsbereich und Vertragspartner</h2>
              <p className="mb-4">
                Diese Allgemeinen Geschäftsbedingungen (AGB) regeln die Nutzung der Plattform uplivion 
                (erreichbar unter www.uplivion.life und app.uplivion.life), die von Arc Rider Ventures GmbH betrieben wird.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-2">Anbieter und Vertragspartner:</h3>
                <p className="mb-2">
                  <strong>Arc Rider Ventures GmbH</strong><br/>
                  Jänickendorfer Str. 64a<br/>
                  14943 Brandenburg<br/>
                  Deutschland
                </p>
                <p className="mb-2">
                  <strong>Handelsregister:</strong> Amtsgericht Berlin Charlottenburg HRB199081B<br/>
                  <strong>Geschäftsführer:</strong> Jakob Jordan
                </p>
                <p>
                  <strong>Kontakt:</strong> support@uplivion.life
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">2. Beschreibung des Dienstes</h2>
              <p className="mb-4">
                uplivion ist eine digitale Plattform zur Erstellung und Verwaltung persönlicher Lebens-Timelines. 
                Nutzer können:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Persönliche Erinnerungen und Ereignisse chronologisch organisieren</li>
                <li>Fotos und Texte zu Lebensereignissen hinzufügen</li>
                <li>Timelines mit Freunden teilen (mit Datenschutzeinstellungen)</li>
                <li>Ihre Lebensgeschichte strukturiert dokumentieren</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">3. Technische Infrastruktur</h2>
              <p className="mb-4">
                uplivion nutzt ausschließlich europäische Dienstleister für optimale Performance, 
                Datenschutz und Rechtskonformität:
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-green-800 mb-2">🇪🇺 100% Europäische Infrastruktur</h4>
                <p className="text-sm text-green-700">
                  Alle Services werden von EU-basierten Anbietern bereitgestellt und unterliegen der DSGVO.
                </p>
              </div>

              <div className="grid md:grid-cols-1 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Website-Hosting & CDN</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Anbieter:</strong> Netlify (Europäische CDN-Edge-Locations)
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Optimierte Performance für europäische Nutzer</li>
                    <li>• Server-Standorte: Frankfurt, London</li>
                    <li>• 99.9% Verfügbarkeitsgarantie</li>
                    <li>• Automatische SSL-Verschlüsselung</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Datenbank & Backend</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Anbieter:</strong> Supabase (Deutschland)
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Datenbank-Hosting in Frankfurt</li>
                    <li>• Echtzeit-Synchronisation</li>
                    <li>• Automatische Backups</li>
                    <li>• End-to-End-Verschlüsselung</li>
                  </ul>
                </div>

                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">E-Mail-Services</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    <strong>Anbieter:</strong> Resend (Irland)
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Transaktionale E-Mails</li>
                    <li>• DSGVO-konforme Zustellung</li>
                    <li>• Hohe Zustellungsraten</li>
                    <li>• Verschlüsselte Übertragung</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">4. Beta-Phase und Verfügbarkeit</h2>
              <p className="mb-4">
                uplivion befindet sich in der Beta-Testphase. Dies bedeutet:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Der Service kann zeitweise nicht verfügbar sein</li>
                <li>Funktionen können sich ohne Vorankündigung ändern</li>
                <li>Datenverlust kann in seltenen Fällen auftreten</li>
                <li>Bugs und Performance-Probleme sind möglich</li>
                <li>Regelmäßige Updates und Verbesserungen werden durchgeführt</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">4.1 Service-Abhängigkeiten</h3>
              <p className="mb-4">
                Die Verfügbarkeit von uplivion hängt von unseren europäischen Infrastruktur-Partnern ab:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Website-Verfügbarkeit:</strong> Abhängig von Netlify CDN (EU)</li>
                <li><strong>Datenbank-Services:</strong> Abhängig von Supabase (Deutschland)</li>
                <li><strong>E-Mail-Funktionen:</strong> Abhängig von Resend (Irland)</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Wartungsfenster</h4>
                <p className="text-sm text-blue-700">
                  Geplante Wartungsarbeiten werden mindestens 24 Stunden im Voraus per E-Mail angekündigt. 
                  Ungeplante Ausfälle werden schnellstmöglich behoben und kommuniziert.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">5. Nutzerkonto und Registrierung</h2>
              <p className="mb-4">
                Zur Nutzung von uplivion ist eine Registrierung erforderlich. Bei der Registrierung müssen Sie:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Wahre und vollständige Angaben machen</li>
                <li>Ein sicheres Passwort wählen</li>
                <li>Ihre Kontaktdaten aktuell halten</li>
                <li>Verantwortung für die Sicherheit Ihres Kontos übernehmen</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">6. Nutzungsrichtlinien</h2>
              <p className="mb-4">Bei der Nutzung von uplivion verpflichten Sie sich:</p>
              <ul className="list-disc pl-6 mb-4">
                <li>Keine illegalen, schädlichen oder beleidigenden Inhalte zu veröffentlichen</li>
                <li>Urheberrechte und Persönlichkeitsrechte Dritter zu respektieren</li>
                <li>Keine Spam- oder Werbeinhalte zu verbreiten</li>
                <li>Die technische Infrastruktur nicht zu beeinträchtigen</li>
                <li>Keine automatisierten Tools ohne Genehmigung zu verwenden</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">7. Abonnement-Pläne und Preise</h2>
              <p className="mb-4">
                uplivion bietet verschiedene Nutzungsmodelle:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Kostenloser Plan:</strong> Grundfunktionen mit Speicherplatz-Beschränkungen</li>
                <li><strong>Premium-Pläne:</strong> Erweiterte Funktionen und mehr Speicherplatz (in Planung)</li>
              </ul>
              <p className="mb-4">
                Preise und Leistungen können sich während der Beta-Phase ändern. 
                Bestehende Nutzer werden über Änderungen rechtzeitig informiert.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">8. Datenschutz und Inhalte</h2>
              <p className="mb-4">
                Der Schutz Ihrer Daten ist uns wichtig. Details zum Datenschutz finden Sie in unserer 
                <Link to="/privacy" className="text-uplivion-blue hover:underline"> Datenschutzerklärung</Link>.
              </p>
              <p className="mb-4">
                Sie behalten die Rechte an Ihren Inhalten. Durch das Hochladen gewähren Sie uns jedoch 
                die technisch notwendigen Nutzungsrechte zur Bereitstellung des Services.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-2">Datenschutz-Garantien</h4>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Alle Daten bleiben in der EU (Deutschland, Irland)</li>
                  <li>• Keine Weitergabe an Dritte ohne Ihre Zustimmung</li>
                  <li>• Vollständige DSGVO-Konformität aller Partner</li>
                  <li>• Jederzeit Löschung oder Export Ihrer Daten möglich</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">9. Haftungsausschluss</h2>
              
              <h3 className="text-xl font-semibold mb-3">9.1 Allgemeine Haftungsbeschränkungen</h3>
              <p className="mb-4">
                Aufgrund der Beta-Phase übernehmen wir nur eingeschränkte Haftung:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Keine Gewährleistung für ununterbrochene Verfügbarkeit</li>
                <li>Keine Haftung für Datenverlust (soweit rechtlich zulässig)</li>
                <li>Beschränkte Haftung für indirekte Schäden</li>
                <li>Haftung für Vorsatz und grobe Fahrlässigkeit bleibt unberührt</li>
              </ul>

              <h3 className="text-xl font-semibold mb-3">9.2 Haftung für Drittanbieter-Services</h3>
              <p className="mb-4">
                Für Ausfälle oder Probleme unserer Infrastruktur-Partner haften wir nur eingeschränkt:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li><strong>Netlify-Ausfälle:</strong> Keine Haftung für CDN-bedingte Nichtverfügbarkeit</li>
                <li><strong>Supabase-Probleme:</strong> Beschränkte Haftung bei Datenbank-Ausfällen</li>
                <li><strong>Resend-Störungen:</strong> Keine Haftung für E-Mail-Zustellungsprobleme</li>
              </ul>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-800 mb-2">Service Level Agreements (SLA)</h4>
                <p className="text-sm text-yellow-700">
                  Während der Beta-Phase gelten noch keine festen SLAs. Wir bemühen uns um 
                  höchstmögliche Verfügbarkeit und transparente Kommunikation bei Problemen.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">10. Kündigung</h2>
              <p className="mb-4">
                Sie können Ihr Konto jederzeit kündigen. Wir behalten uns vor, Konten bei Verstößen 
                gegen diese AGB zu sperren oder zu löschen.
              </p>
              
              <h3 className="text-xl font-semibold mb-3">10.1 Datenexport bei Kündigung</h3>
              <p className="mb-4">
                Bei Konto-Löschung haben Sie das Recht auf:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>Vollständigen Export Ihrer Timeline-Daten</li>
                <li>Download aller hochgeladenen Bilder</li>
                <li>Kopie Ihrer Profil-Informationen</li>
                <li>30 Tage Bedenkzeit vor endgültiger Löschung</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">11. Änderungen der AGB</h2>
              <p className="mb-4">
                Wir können diese AGB jederzeit ändern. Über wesentliche Änderungen werden Sie 
                per E-Mail oder über die Plattform informiert.
              </p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">Änderungsverfahren</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Mindestens 14 Tage Vorlaufzeit bei wesentlichen Änderungen</li>
                  <li>• E-Mail-Benachrichtigung an alle registrierten Nutzer</li>
                  <li>• Widerspruchsrecht mit Kündigungsmöglichkeit</li>
                  <li>• Klare Darstellung der Änderungen</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">12. Anwendbares Recht</h2>
              <p className="mb-4">
                Es gilt deutsches Recht. Gerichtsstand ist Brandenburg an der Havel, soweit rechtlich zulässig.
              </p>
              
              <h3 className="text-xl font-semibold mb-3">12.1 EU-Recht</h3>
              <p className="mb-4">
                Zusätzlich finden Anwendung:
              </p>
              <ul className="list-disc pl-6 mb-4">
                <li>EU-Datenschutz-Grundverordnung (DSGVO)</li>
                <li>EU-Verbraucherrechte-Richtlinie</li>
                <li>EU-Richtlinie über digitale Inhalte</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold mb-4">13. Kontakt</h2>
              <p className="mb-4">
                Bei Fragen zu diesen AGB wenden Sie sich an:
              </p>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-2">Geschäftliche Anfragen</h4>
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Arc Rider Ventures GmbH</strong><br/>
                      Jänickendorfer Str. 64a<br/>
                      14943 Brandenburg<br/>
                      Deutschland
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Geschäftsführer:</strong> Jakob Jordan<br/>
                      <strong>HRB:</strong> 199081B (AG Berlin Charlottenburg)
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Support-Kontakt</h4>
                    <p className="text-sm text-gray-600">
                      <strong>Alle Anfragen:</strong> support@uplivion.life
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-8 pt-8 border-t">
            <Button asChild>
              <Link to="/">Zur Startseite</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
